import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import {
  DropdownOption,
  GroupedDropdownOption,
} from '../../../../../shared/data-types/dropdown-option';
import { map, switchMap } from 'rxjs/operators';
import { ProjectsHttpService } from '../../../../../core/state/projects/projects-http.service';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import {
  BudgetOptions,
  ExpenditureOptions,
  InvoiceType,
} from '../../../../../core/constants-repository/project-calculation.constants';
import { Store } from '@ngrx/store';
import { selectProjectById } from '../../../../../core/state/projects/projects.selectors';
import { ProjectTransformationService } from '../../../../../core/services/project-transformation.service';
import { recordToGroupedDropdownOptions } from '../../../../../shared/functions/dropdown-functions';
import { ProjectStatusFilter } from '../../../../project/shared/project-detail-types';

@Injectable()
export class CreateInvoiceDialogService {
  // DI
  private readonly projectsHttpService = inject(ProjectsHttpService);
  private readonly projectTransformationService = inject(ProjectTransformationService);
  private readonly store = inject(Store);

  public loadGroupedProjects(): Observable<GroupResult[] | GroupedDropdownOption[]> {
    return this.projectsHttpService.getProjects(null, ProjectStatusFilter.Active).pipe(
      // map to dropdown options and then group for Kendo
      switchMap((projects) =>
        recordToGroupedDropdownOptions(
          this.projectTransformationService.groupProjectsByClients(projects),
        ),
      ),
      map((options) => groupBy(options, [{ field: 'category' }])),
    );
  }

  public deriveExpenditureOptions(project: ProjectModel): DropdownOption[] {
    const options: DropdownOption[] = [];
    const isExpenditureRate =
      project.expenditureType === ExpenditureOptions.HourlyRateFixed ||
      project.expenditureType === ExpenditureOptions.HourlyRatePerMember ||
      project.expenditureType === ExpenditureOptions.HourlyRatePerTask;

    if (isExpenditureRate) {
      options.push({ text: 'Aufwand (Time & Material)', value: InvoiceType.TimeAndMaterial });
    }

    if (project.budgetType === BudgetOptions.TotalProjectMonetaryBudget) {
      options.push({
        text: 'Gesamte Projektkosten',
        value: BudgetOptions.TotalProjectMonetaryBudget,
      });
    } else if (project.budgetType === BudgetOptions.MonetaryPerTask) {
      options.push({ text: 'Budget pro Aufgabe', value: BudgetOptions.MonetaryPerTask });
    }

    return options.length ? options : [{ text: '—', value: null }];
  }

  public async getProject(projectId: number): Promise<ProjectModel | undefined> {
    return await firstValueFrom(this.store.select(selectProjectById(projectId)));
  }
}
